// Dependencies
import { createFileRoute } from "@tanstack/react-router";
import { PageShell } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { CardGrid } from "@/components/CardGrid";
import { LangLink as Link } from "@/components/LangLink";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";

// Icons
import { BadgeCheck, Rocket, Globe } from "lucide-react";

// Images
import heroImage from "@/assets/hero-sobre.jpg";
import journey2020 from "@/assets/journey/2020.jpg";
import journey2021 from "@/assets/journey/2021.png";
import journey2022 from "@/assets/journey/2022.png";
import journey2023 from "@/assets/journey/2023.png";
import journey2024 from "@/assets/journey/2024.png";
import journey2025 from "@/assets/journey/2025.png";
import journey2026 from "@/assets/journey/2026.png";
import journey20262 from "@/assets/journey/2026-2.jpg";
import eed from "@/assets/eed.png";
import ped from "@/assets/ped.png";
import bgVideo from "@/assets/video/bg.mp4";

export const Route = createFileRoute("/$lang/about")({
  head: () => ({
    meta: [
      { title: i18n.t("about:metaTitle", "About — BIZU Space") },
      {
        name: "description",
        content: i18n.t("about:metaDesc",
          "Learn about BIZU Space — a Brazilian vertical developer of rocket and satellite technologies based in São José dos Campos.",
        ),
      },
      { property: "og:title", content: i18n.t("about:metaTitle", "About — BIZU Space") },
      {
        property: "og:description",
        content: i18n.t("about:metaOgDesc",
          "Learn about BIZU Space — a Brazilian vertical developer of rocket and satellite technologies.",
        ),
      },
      { property: "og:url", content: "/about" },
    ],
    links: [{ rel: "canonical", href: "https://bizu.space/about" }],
  }),
  component: About,
});

function About() {
  const { t } = useTranslation("about");

  const principleCards = [
    {
      icon: BadgeCheck,
      title: t("principles.discipline.title", "Conscious Discipline"),
      desc: t("principles.discipline.desc",
        "One of our core values, shaped by our founders’ experience at ITA, is Conscious Discipline. We do what is right because it is right — not because someone is watching.",
      ),
    },
    {
      icon: Rocket,
      title: t("principles.capability.title", "Independent Capability"),
      desc: t("principles.capability.desc",
        "We develop technology in-house to ensure autonomy, resilience and long-term national competitiveness.",
      ),
    },
    {
      icon: Globe,
      title: t("principles.commitment.title", "Long-Term Commitment"),
      desc: t("principles.commitment.desc",
        "We are building not only rockets, but a sustainable aerospace ecosystem for future generations.",
      ),
    },
  ];

  const journey = [
    {
      year: "2020",
      title: t("journey.y2020.title", "The Beginning"),
      description: t("journey.y2020.desc",
        "A movement born in the pandemic\n\nFounded in 2020 as a spin-off from ITA Rocket Design, a student rocketry team at Instituto Tecnológico de Aeronáutica (ITA). Originally a group of engineers and students collaborating on experimental rocket projects, the initiative evolved into building the team that would become BIZU Space.\n\nUnited by a shared vision, they began to work toward a shared goal: to accelerate access to space through the development of rocket technologies.",
      ),
      image: journey2020,
    },
    {
      year: "2021",
      title: t("journey.y2021.title", "From Concept to Hardware"),
      description: t("journey.y2021.desc",
        "First Tests. First Hardware. Real Engineering.\n\nDevelopment moved from online collaboration to real hardware manufacturing.\n\n• Rocket components built in a garage workshop\n• 30+ solid motor tests performed\n• Electronic and recovery systems validated\n• Balloon drop tests conducted with Clube do Balão de Boituva\n\nBizu proved it could transform engineering into flight-ready systems.",
      ),
      image: journey2021,
    },
    {
      year: "2022",
      title: t("journey.y2022.title", "Official Foundation & First Flights"),
      description: t("journey.y2022.desc",
        "From Initiative to Aerospace Company.\n\nIn 2022, Bizu Tecnologias Aeroespaciais e Serviços LTDA was officially incorporated.\n\nThat same year:\n• Headquarters established in São José dos Campos\n• Full development of Beija-Flor 1 (BF-1)\n• Two official launch operations performed\n• NOTAM issued by the Brazilian Air Force\n\nBizu transitioned from passion project to aerospace company.",
      ),
      image: journey2022,
    },
    {
      year: "2023",
      id: "bcp",
      title: t("journey.y2023.title", "Propulsion Independence & National Recognition"),
      description: t("journey.y2023.desc",
        `Propulsion Independence

      Developing ITAR-Free Solid Propulsion

      In 2023, BIZU Space developed the Bizu Composite Propellant (BCP), a fully Brazilian solid propulsion system created in response to import restrictions and national technological constraints.

      • ~1 year of in-house development
      • 50+ motors tested
      • From prototype to full-scale BF-1
      • Technology Readiness Level 6 achieved

      BCP represents technological sovereignty and in-house propulsion capability.

      National Recognition (MLBR — Brazilian Microlauncher)

      Selected for the Brazilian Microlauncher Program.

      In november 2023, BIZU Space was selected to support the Brazilian Microlauncher (MLBR) program under FINEP, in partnership with CENIC. The project would officialy start in January 2024.

      The MLBR program aims to fulfill a Brazilian ambition dating back to 1979: launching a Brazilian satellite from Brazilian territory on a Brazilian-built rocket.

      Within MLBR, BIZU Space contributes with:

      • Systems Engineering
      • Aerospace Engineering
      • Launch Operations
      • Development of the ARION Liquid Rocket Engine

      BIZU Space became part of Brazil’s national space strategy.`,
      ),
      image: journey2023,
    },
    {
      year: "2024",
      title: t("journey.y2024.title", "Infrastructure & Vertical Integration"),
      description: t("journey.y2024.desc",
        "Building the Foundation for Scalable Growth.\n\nIn 2024, BIZU Space entered a new phase of expansion.\n\n• Creation of the T8 Test Facility\n• Development of ARION liquid engine\n• Recruitment of multidisciplinary engineering team\n• Full vertical integration established\n\nDesign → Manufacture → Test → Launch",
      ),
      image: journey2024,
    },
    {
      year: "2025",
      title: t("journey.y2025.title", "Capability Demonstrated Through Testing"),
      description: t("journey.y2025.desc",
        "The year to prove BIZU Space's capability.\n\nIn 2025, BIZU Space started the extensive test campaign of liquid rocket engines to develop ARION. Only in 2025, BIZU Space performed:\n\n• 200+ kg of high-test peroxide (HTP) concentrated in-house\n• 80+ metallic pieces manufactured in house\n• 57 liquid rocket engine tests - 10 tests in ARION real scale engine\n• 50+ complex components designed in CAD\n• 16 iterations in combustion chambers\n\nBizu Space has a culture of vertical integration and learning by doing. This is real rocket engineering, not just PowerPoint.",
      ),
      image: journey2025,
    },
    {
      year: "2026",
      title: t("journey.y2026.title", "The First Liquid Propelled Rocket in Brazil"),
      description: t("journey.y2026.desc",
        "Doing what a rocket company should do: launching rockets.\n\n2026 started as a challenging year with the target of final qualifications of Arion's first flight model and launching a sounding rocket with this engine. This rocket was the first in Brazilian history to use only liquid propulsion.\n\nWe are driven by challenges, innovation, and the ambition to build real rocket technology in Brazil.",
      ),
      image: journey2026,
      image2: journey20262,
    },
  ];

  return (
    <PageShell>
      {/* HERO */}
      <section className="relative min-h-[520px] flex items-end overflow-hidden border-b border-border">
        <img
          src={heroImage}
          alt={t("imageAlt", "BIZU Space about")}
          className="absolute inset-0 w-full h-full object-cover"
          width={1920}
          height={900}
        />
      </section>

      {/* ABOUT / CERTIFICATES */}
      <section className="relative overflow-hidden bg-[#0b0b0b] py-4">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.08),transparent_45%)]" />
        <div className="container-bizu relative">
          <Reveal effect="up" className="mx-auto max-w-5xl text-center">
            <h1 className="display mt-5 text-4xl uppercase leading-tight text-white md:text-6xl">
              {t("heroTitle", "Turning a National Dream Into Launch Reality")}
            </h1>
            <p className="mx-auto mt-8 max-w-4xl text-xl leading-relaxed text-white/75">
              {t("heroSubtitle",
                "From a remote rocketry initiative during the pandemic to a nationally selected aerospace company, Bizu Space is building Brazil’s sovereign access to space.",
              )}
            </p>
          </Reveal>

          <Reveal
            stagger
            className="mt-10 grid gap-px overflow-hidden rounded-3xl border border-white/10 bg-white/10 md:grid-cols-3"
          >
            <div className="bg-black/80 p-8 text-center">
              <div className="mb-4 text-5xl font-bold text-accent">150+</div>
              <h3 className="display text-xl text-white">{t("stats.tested.title", "Rocket Motors/Engines Tested")}</h3>
              <p className="mt-4 text-sm leading-relaxed text-white/60">
                {t("stats.tested.desc", "Extensive solid and liquid propulsion test campaigns.")}
              </p>
            </div>

            <div className="bg-black/80 p-8 text-center">
              <div className="mb-4 text-5xl font-bold text-accent">5</div>
              <h3 className="display text-xl text-white">{t("stats.launches.title", "Official Launches")}</h3>
              <p className="mt-4 text-sm leading-relaxed text-white/60">
                {t("stats.launches.desc", "Launch operations performed with BIZU Space rockets.")}
              </p>
            </div>

            <div className="bg-black/80 p-8 text-center">
              <div className="mb-4 text-5xl font-bold text-accent">2026</div>
              <h3 className="display text-xl text-white">{t("stats.milestone.title", "MAJOR MILESTONE")}</h3>
              <p className="mt-4 text-sm leading-relaxed text-white/60">
                {t("stats.milestone.desc", "Flight of the first rocket designed in Brazil to rely exclusively on liquid propulsion")}
              </p>
            </div>
          </Reveal>
          <Reveal
            effect="up"
            className="mx-auto py-20 max-w-6xl px-8 py-14 text-center shadow-[0_20px_80px_rgba(0,0,0,0.35)] backdrop-blur-xl md:px-16 md:py-16"
          >
            <div className="flex flex-wrap items-center justify-center gap-8">
              <img src={ped} alt="PED Certificate" className="h-32 w-auto object-contain md:h-40" />
              <img src={eed} alt="EED Certificate" className="h-32 w-auto object-contain md:h-40" />
            </div>
            <div className="mx-auto mt-10 max-w-3xl">
              <h3 className="display text-3xl uppercase leading-tight text-white md:text-5xl">
                {t("certificate.title", "Strategic Company Certificate")}
              </h3>
              <p className="mt-6 text-lg leading-relaxed text-white/70 md:text-xl">
                {t("certificate.desc",
                  "BIZU Space is recognized as part of Brazil’s strategic space ecosystem, reinforcing our commitment to national technological autonomy and sovereign access to space.",
                )}
              </p>
            </div>
            <div className="flex justify-center mt-8">
              <Link to="/strategic-certification" className="btn-pill">
                {t("certificate.btn", "Learn more")}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* OUR PRINCIPLES */}
      <section className="relative overflow-hidden py-28">
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 h-full w-full object-cover"
        >
          <source src={bgVideo} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black/75" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.08),transparent_55%)]" />

        <div className="container-bizu relative z-10">
          <Reveal effect="scale" className="mx-auto mb-16 max-w-3xl text-center">
            <span className="section-tag">{t("principles.tag", "Our Principles")}</span>

            <h2 className="display mt-5 text-4xl uppercase text-white md:text-5xl">
              {t("principles.title", "The Principles That Guide Us")}
            </h2>

            <p className="mt-6 text-white/60">
              {t("principles.subtitle", "Engineering with responsibility, discipline and long-term commitment.")}
            </p>
          </Reveal>
          <CardGrid items={principleCards} />
        </div>
      </section>

      {/* JOURNEY */}
      <section className="relative overflow-hidden bg-[#161616] py-28">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.06),transparent_60%)]" />

        <div className="container-bizu relative max-w-7xl">
          <Reveal effect="scale" className="mb-20 text-center">
            <h2 className="display text-4xl md:text-6xl uppercase tracking-wider text-white">
              {t("journey.title", "Our Journey")}
            </h2>
          </Reveal>

          <div className="relative">
            <div className="absolute left-1/2 top-0 bottom-0 hidden w-px -translate-x-1/2 bg-accent lg:block" />
            <div className="grid gap-y-24 lg:grid-cols-2 lg:gap-x-20">
              {journey.map((item, idx) => {
                const isLeft = idx % 2 === 0;

                return (
                  <Reveal key={item.year} effect="up">
                    <div
                      id={item.id || item.year}
                      className={`scroll-mt-24 relative ${isLeft ? "lg:pr-12 lg:text-right" : "lg:pl-12 lg:text-left lg:mt-40"
                        }`}
                    >
                      <div
                        className={`mb-5 text-[26px] font-medium tracking-[0.08em] text-accent ${isLeft ? "text-right" : "text-left"
                          }`}
                      >
                        {item.year}
                      </div>
                      <div className={`grid gap-4 ${item.image2 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                        <div
                          className="overflow-hidden border border-white/20 bg-black shadow-[0_0_80px_rgba(255,255,255,0.03)]"
                          style={{
                            clipPath: isLeft
                              ? "polygon(15% 0, 100% 0, 100% 100%, 0 100%, 0 22%)"
                              : "polygon(0 0, 85% 0, 100% 22%, 100% 100%, 0 100%)",
                          }}
                        >
                          <img
                            src={item.image}
                            alt={item.title}
                            loading="lazy"
                            className="h-[240px] w-full object-cover"
                          />
                        </div>
                        {item.image2 && (
                          <div
                            className="overflow-hidden border border-white/20 bg-black shadow-[0_0_80px_rgba(255,255,255,0.03)]"
                            style={{
                              clipPath: isLeft
                                ? "polygon(15% 0, 100% 0, 100% 100%, 0 100%, 0 22%)"
                                : "polygon(0 0, 85% 0, 100% 22%, 100% 100%, 0 100%)",
                            }}
                          >
                            <img
                              src={item.image2}
                              alt={item.title}
                              loading="lazy"
                              className="h-[240px] w-full object-cover"
                            />
                          </div>
                        )}
                      </div>
                      <h3 className="display mt-4 text-[32px] font-extrabold text-white uppercase tracking-widest leading-tight">
                        {item.title}
                      </h3>
                      <div className="mt-6 space-y-3 text-[17px] font-light leading-[1.75] text-white/90">
                        {item.description.split("\n").map((para, i) => (
                          <p key={i} className={para.startsWith("•") ? "ml-4" : ""}>
                            {para}
                          </p>
                        ))}
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
